import { useMutation } from "@apollo/client";
import { InformationCircleIcon, XIcon } from "@heroicons/react/outline";
import { withErrorBoundary } from "@sentry/react";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  UpdateMeetingGroupNewPageMutationMutation,
  UpdateMeetingGroupNewPageMutationMutationVariables,
} from "types/graphql-schema";

import updateMeetingGroupMutation from "@apps/meeting-new/graphql/update-meeting-group-mutation";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";

const DetectedOneononeBanner = ({
  meeting,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
}) => {
  const label = useLabel();

  const [updateMeetingGroup, { loading }] = useMutation<
    UpdateMeetingGroupNewPageMutationMutation,
    UpdateMeetingGroupNewPageMutationMutationVariables
  >(updateMeetingGroupMutation);

  if (
    !meeting.meetingGroup ||
    meeting.meetingGroup.detectedOneononeBannerDismissed ||
    meeting.meetingGroup.isFormalOneonone ||
    !meeting.isManagerAndReportOneonone
  ) {
    return null;
  }

  const meetingGroupId = meeting.meetingGroup.id;

  const handleDismiss = () => {
    updateMeetingGroup({
      variables: { meetingGroupId, detectedOneononeBannerDismissed: true },
    });
  };

  const handleConvertToOneonOne = () => {
    updateMeetingGroup({
      variables: {
        meetingGroupId,
        isFormalOneonone: true,
        detectedOneononeBannerDismissed: true,
      },
    });
  };

  return (
    <div className="flex items-start gap-3 justify-between px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg mb-4">
      <InformationCircleIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800">
          Is this a {label("1-on-1")} meeting?
        </p>
        <p className="text-sm text-gray-600 mt-2">
          Our system detects that this meeting might be a {label("1-on-1")}. Is
          that correct, or would you like to keep it as a Regular Meeting?
        </p>
        <div className="flex items-center gap-3 mt-4">
          <Button
            theme={buttonTheme.primary}
            text={`Convert to ${label("1-on-1")}`}
            disabled={loading}
            onClick={handleConvertToOneonOne}
          />
          <Button
            theme={buttonTheme.default}
            text={`Keep as Regular Meeting`}
            disabled={loading}
            onClick={handleDismiss}
          />
        </div>
      </div>
      <button
        className="text-gray-400 hover:text-gray-600 shrink-0 mt-0.5"
        onClick={handleDismiss}
        aria-label="Dismiss banner"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

const DetectedOneononeBannerWithSentry = withErrorBoundary(
  DetectedOneononeBanner,
  {
    fallback: <Error description={"The meeting could not be rendered."} />,
  },
);

export default DetectedOneononeBannerWithSentry;
